import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  SELLABLE_SALE_CYCLES,
  effectiveCommercialMode,
  type BillingCycle,
  type CommercialModeTarget,
  type CrmPartner,
  type Subscription,
  CommercialMode,
  CommercialModeChangeOutcomeSchema,
  CrmPartnerStatusSchema,
  SubscriptionItemOrigin,
} from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { cancelCommercialModeTransitionCallable, changeTenantCommercialModeCallable } from '@/lib/callables';
import { listCrmPartners } from '@/lib/firestoreCrmPartners';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { targetsFor } from './commercialModeTargets';

type Props = {
  open: boolean;
  tenantId: string;
  tenantName: string;
  countryCode: string;
  subscription: Subscription;
  onClose: () => void;
};

type Row = { sku: string; amount: string; remit: string };

/** Reais digitados (vírgula ou ponto) → centavos inteiros; inválido ou negativo → `null`. */
const toCents = (v: string): number | null => {
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};
/** Centavos → texto em reais com 2 casas, para pré-preencher o formulário. */
const fromCents = (c: number): string => (c / 100).toFixed(2);

/**
 * Troca o tipo comercial de um tenant (parceiro ↔ venda direta ↔ cortesia,
 * matriz 9.18 da #679). Com uma troca pendente, só oferece desfazê-la.
 *
 * Origem: #743 — 2026-09-25 17:32 +0200 (ativação pelo parceiro, pós-venda
 * sem Stripe e matriz 9.18).
 *
 * Por quê: o que cada célula faz — agora (`APPLIED`), no fim do período
 * (`SCHEDULED`, DIRECT → PARTNER com assinatura paga) ou ao pagar
 * (`LINK_CREATED`, PARTNER → DIRECT) — é decisão do servidor
 * (`changeTenantCommercialMode`); a tela mostra o resultado e, no
 * `LINK_CREATED`, fica aberta com o link para copiar.
 *
 * Contrato: destinos por `targetsFor(from)`. Para PARTNER exige parceiro
 * ATIVO do mesmo país (`listCrmPartners`, lido ao abrir). Para PARTNER e
 * DIRECT exige a composição: ciclo (`SELLABLE_SALE_CYCLES`) e, por item
 * vendido (`items[]` sem `GRANT` e sem `removed_at`; sem itens, um `BASE` a
 * 0), valor e — só PARTNER — repasse. Motivo ≥ 3 caracteres. Confirmação
 * (`AdminConfirm`) antes de enviar. Desfazer: `cancelCommercialModeTransition`.
 * Valores digitados em REAIS/unidades (`toCents` aceita vírgula) e enviados
 * em CENTAVOS.
 *
 * O servidor exige `ADMIN_PERMISSIONS.manageSubscriptions` (ROOT/FINANCE) nos
 * dois callables. A lista de parceiros é leitura direta de `crm_partners`
 * (coleção inteira, sem índice composto; rules `match /crm_partners/{partnerId}`
 * → `isSuperAdmin()`); falha vira `Alert` `billing.commercialMode.errors.partners`
 * dentro do modal. Erro do envio e do desfazer também vira `Alert` no modal
 * principal (#1021 — o toast ficava atrás do overlay, #174).
 *
 * Usado por: `BillingActions` → `routes/TenantDetailPage.tsx` (rota
 * `/tenants/:id`, menu Tenants), admin web. Spec:
 * `docs/screens/tenant-detail-admin.md`.
 *
 * Dados: `ChangeTenantCommercialModeInput/Output` e `CommercialModeTarget`
 * (`packages/types/src/commercialModeTransition.ts`); lê `subscription.items[]`
 * e `partner_transition` do doc `billing/subscription`. `effectiveAt` epoch ms.
 *
 * Dicas: o formulário reinicia a cada abertura (`useEffect` em `open`, com
 * `exhaustive-deps` desligado de propósito).
 *
 * Histórico:
 *  - 2026-09-27 17:30 +0200 — (#761) comparações pelas constantes.
 *  - 2026-10-10 14:40 +0200 — (#798) rota, permissão e leitura de parceiros (#796).
 *  - 2026-10-13 10:55 +0200 — (#935, item 22) status do parceiro e resultado
 *    da troca comparados pelas constantes (`CrmPartnerStatusSchema`,
 *    `CommercialModeChangeOutcomeSchema`), não pelo texto.
 *  - 2026-10-25 15:00 +0100 — (#1021) falha ao listar parceiros, ao enviar e ao
 *    desfazer saem em `Alert` dentro do modal, não em toast atrás do overlay.
 *
 * Issues: #743, #761, #798, #935, #1021 (erro dentro do modal; achado do CRM, #174).
 */
export function ChangeCommercialModeDialog({
  open,
  tenantId,
  tenantName,
  countryCode,
  subscription,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const from = effectiveCommercialMode(subscription);
  const targets = targetsFor(from);
  const pending = subscription.partner_transition ?? null;
  const [to, setTo] = useState<CommercialModeTarget | null>(targets[0] ?? null);
  const [partners, setPartners] = useState<CrmPartner[]>([]);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [cycle, setCycle] = useState<BillingCycle>(subscription.billing_cycle);
  const [rows, setRows] = useState<Row[]>([]);
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  // #1021 (2026-10-25 15:00 +0100): erros mostrados DENTRO do modal principal — o toast ficava
  // atrás do overlay. `partnersError` é da leitura inicial; `error`, do envio/desfazer.
  const [partnersError, setPartnersError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTo(targets[0] ?? null);
    setReason('');
    setCheckoutUrl(null);
    setConfirmOpen(false);
    setPartnersError(null);
    setError(null);
    setCycle(subscription.billing_cycle);
    const sold = (subscription.items ?? []).filter((it) => it.origin !== SubscriptionItemOrigin.GRANT && it.removed_at == null);
    setRows(
      (sold.length > 0 ? sold : [{ sku: 'BASE', amount_cents: 0, remit_cents: 0 }]).map((it) => ({
        sku: it.sku,
        amount: fromCents(it.amount_cents),
        remit: 'remit_cents' in it && typeof it.remit_cents === 'number' ? fromCents(it.remit_cents) : '',
      })),
    );
    listCrmPartners()
      .then((list) => {
        // #935 (item 22): pela constante do conceito, não pelo texto (#761).
        setPartners(
          list.filter((p) => p.status === CrmPartnerStatusSchema.enum.ACTIVE && p.countryCode === countryCode),
        );
      })
      .catch((err: unknown) => {
        // Auditoria 2026-10-25 15:00 +0100 (#1021): era toast, atrás do overlay deste modal (#174).
        // 2ª auditoria (15:52): e descartava a causa — leitura direta do Firestore, fora do wrapper
        // `callable()`, então nada a reportava.
        reportNonFatal('admin_commercial_mode_partners_load_failed', serializeError(err));
        setPartnersError(t('billing.commercialMode.errors.partners'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reabrir o diálogo reinicia o formulário
  }, [open]);

  const needsRemit = to === CommercialMode.PARTNER;
  const needsItems = to === CommercialMode.PARTNER || to === CommercialMode.DIRECT;
  const parsedItems = useMemo(
    () =>
      rows.map((r) => ({
        sku: r.sku,
        amountCents: toCents(r.amount),
        remitCents: needsRemit ? toCents(r.remit) : 0,
      })),
    [rows, needsRemit],
  );
  const itemsValid = !needsItems || parsedItems.every((it) => it.amountCents !== null && it.remitCents !== null);
  const canSubmit =
    to !== null && reason.trim().length >= 3 && itemsValid && (to !== CommercialMode.PARTNER || partnerId !== null);

  /**
   * Envia a troca ao `changeTenantCommercialMode` (depois do `AdminConfirm`).
   *
   * Origem: #743 — 2026-09-25 17:32 +0200.
   *
   * Contrato: `items` (+ `billingCycle`) só vão para PARTNER/DIRECT;
   * `remitCents` só para PARTNER; `partnerId` só para PARTNER. Saída
   * `outcome`: `LINK_CREATED` → diálogo fica aberto mostrando `checkoutUrl`;
   * `SCHEDULED` → toast com `effectiveAt` (epoch ms) e fecha; `APPLIED` → fecha.
   * Erro → fecha o `AdminConfirm` e mostra `err.message` (sem tradução por
   * `details.code`) num `Alert` do modal principal, que reaparece (#1021).
   *
   * Dados — input (valores em CENTAVOS):
   *   { "tenantId": "tenant-1", "to": "PARTNER", "reason": "...",
   *     "partnerId": "partner-1", "billingCycle": "monthly",
   *     "items": [{ "sku": "BASE", "amountCents": 12990, "remitCents": 3000 }] }
   *
   * Usado por: `AdminConfirm` deste diálogo.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   *  - 2026-10-25 15:00 +0100 — (#1021) erro em `Alert` no modal, não em toast.
   */
  async function submit(): Promise<void> {
    if (to === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const items = needsItems
        ? parsedItems.map((it) =>
            needsRemit
              ? { sku: it.sku, amountCents: it.amountCents ?? 0, remitCents: it.remitCents ?? 0 }
              : { sku: it.sku, amountCents: it.amountCents ?? 0 },
          )
        : undefined;
      const out = await changeTenantCommercialModeCallable({
        tenantId,
        to,
        reason: reason.trim(),
        ...(to === CommercialMode.PARTNER && partnerId ? { partnerId } : {}),
        ...(items ? { items, billingCycle: cycle } : {}),
      });
      setConfirmOpen(false);
      // #935 (item 22): constante do resultado, não o texto (#761).
      if (out.outcome === CommercialModeChangeOutcomeSchema.enum.LINK_CREATED && out.checkoutUrl) {
        setCheckoutUrl(out.checkoutUrl);
        toast.success(t('billing.commercialMode.success.link'));
        return;
      }
      toast.success(
        out.outcome === 'SCHEDULED'
          ? t('billing.commercialMode.success.scheduled', {
              date: out.effectiveAt ? new Date(out.effectiveAt).toLocaleDateString() : '—',
            })
          : t('billing.commercialMode.success.applied'),
      );
      onClose();
    } catch (err) {
      setConfirmOpen(false);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): o confirm fecha e o modal principal reabre; o
      // toast que ia aqui ficava atrás do overlay dele (#174). O erro vai para o `Alert` do modal.
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Desfaz a troca pendente (`partner_transition`) via `cancelCommercialModeTransition`.
   *
   * Origem: #743 — 2026-09-25 17:32 +0200.
   *
   * Contrato: input `{ "tenantId": "tenant-1" }`; sucesso → toast e fecha;
   * erro → `err.message` no `Alert` do modal (#1021). Sem confirmação extra (só
   * desfaz o agendado).
   *
   * Usado por: botão "Desfazer" deste diálogo, visível só com troca pendente.
   *
   * Histórico:
   *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
   *  - 2026-10-25 15:00 +0100 — (#1021) erro em `Alert` no modal, não em toast.
   */
  async function undoPending(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await cancelCommercialModeTransitionCallable({ tenantId });
      toast.success(t('billing.commercialMode.success.undone'));
      onClose();
    } catch (err) {
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era toast, atrás do overlay do modal (#174).
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        visible={open && !confirmOpen}
        // #1021 (2ª auditoria 2026-10-25 15:52 +0100): com a ação em voo, Esc/X/clique fora
        // fechavam o modal e a falha caía no `Alert` dele, já fechado — ninguém a via.
        onDismiss={(): void => { if (!(submitting)) { onClose(); } }}
        size="large"
        header={t('billing.commercialMode.title', { tenantName })}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClose} disabled={submitting}>
                {t('tenantDetail.actions.cancel')}
              </Button>
              {pending ? (
                <Button variant="primary" loading={submitting} onClick={(): void => void undoPending()}>
                  {t('billing.commercialMode.undoButton')}
                </Button>
              ) : checkoutUrl === null ? (
                <Button
                  variant="primary"
                  disabled={!canSubmit || submitting}
                  onClick={(): void => {
                    setConfirmOpen(true);
                  }}
                >
                  {t('billing.commercialMode.submit')}
                </Button>
              ) : null}
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">{t('billing.commercialMode.current', { mode: t(`billing.commercialMode.mode.${from}`) })}</Box>
          {pending ? (
            <Alert type="info" header={t('billing.commercialMode.pendingTitle')}>
              {t(`billing.commercialMode.pending.${pending.to}`)}
            </Alert>
          ) : checkoutUrl ? (
            <Alert type="success" header={t('billing.commercialMode.linkTitle')}>
              <SpaceBetween size="xs">
                <Box variant="p">{t('billing.commercialMode.linkHint')}</Box>
                <CopyToClipboard
                  variant="inline"
                  textToCopy={checkoutUrl}
                  copyButtonText={t('billing.commercialMode.copy')}
                  copySuccessText={t('billing.commercialMode.copied')}
                  copyErrorText={t('tenantDetail.actions.errorGeneric')}
                />
              </SpaceBetween>
            </Alert>
          ) : targets.length === 0 ? (
            <Alert type="warning">{t('billing.commercialMode.noTargets')}</Alert>
          ) : (
            <>
              <FormField label={t('billing.commercialMode.toLabel')}>
                <Select
                  selectedOption={to ? { value: to, label: t(`billing.commercialMode.mode.${to}`) } : null}
                  options={targets.map((v) => ({ value: v, label: t(`billing.commercialMode.mode.${v}`) }))}
                  onChange={({ detail }): void => {
                    setTo(detail.selectedOption.value as CommercialModeTarget);
                  }}
                />
              </FormField>
              <Box variant="small" color="text-body-secondary">
                {to ? t(`billing.commercialMode.effect.${from}_${to}`) : null}
              </Box>

              {to === CommercialMode.PARTNER && (
                <FormField label={t('billing.commercialMode.partnerLabel')}>
                  <Select
                    selectedOption={
                      partnerId
                        ? { value: partnerId, label: partners.find((p) => p.id === partnerId)?.name ?? partnerId }
                        : null
                    }
                    options={partners.map((p) => ({ value: p.id, label: p.name }))}
                    empty={t('billing.commercialMode.noPartners')}
                    placeholder={t('billing.commercialMode.partnerPlaceholder')}
                    onChange={({ detail }): void => {
                      setPartnerId(detail.selectedOption.value ?? null);
                    }}
                  />
                </FormField>
              )}

              {needsItems && (
                <>
                  <FormField label={t('billing.commercialMode.cycleLabel')}>
                    <Select
                      selectedOption={{ value: cycle, label: t(`billing.commercialMode.cycle.${cycle}`) }}
                      options={SELLABLE_SALE_CYCLES.map((c) => ({
                        value: c,
                        label: t(`billing.commercialMode.cycle.${c}`),
                      }))}
                      onChange={({ detail }): void => {
                        setCycle(detail.selectedOption.value as BillingCycle);
                      }}
                    />
                  </FormField>
                  {rows.map((row, i) => (
                    <ColumnLayout key={row.sku} columns={needsRemit ? 3 : 2}>
                      <FormField label={t('billing.commercialMode.skuLabel')}>
                        <Box>{row.sku}</Box>
                      </FormField>
                      <FormField label={t('billing.commercialMode.amountLabel')}>
                        <Input
                          inputMode="decimal"
                          value={row.amount}
                          onChange={({ detail }): void => {
                            setRows((rs) => rs.map((r, j) => (j === i ? { ...r, amount: detail.value } : r)));
                          }}
                        />
                      </FormField>
                      {needsRemit && (
                        <FormField label={t('billing.commercialMode.remitLabel')}>
                          <Input
                            inputMode="decimal"
                            value={row.remit}
                            onChange={({ detail }): void => {
                              setRows((rs) => rs.map((r, j) => (j === i ? { ...r, remit: detail.value } : r)));
                            }}
                          />
                        </FormField>
                      )}
                    </ColumnLayout>
                  ))}
                </>
              )}

              <FormField label={t('billing.commercialMode.reasonLabel')}>
                <Input
                  value={reason}
                  onChange={({ detail }): void => {
                    setReason(detail.value);
                  }}
                />
              </FormField>
            </>
          )}
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erros dentro do modal, junto do footer (#174). */}
          {partnersError !== null && <Alert type="error">{partnersError}</Alert>}
          {error !== null && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Modal>

      <AdminConfirm
        visible={confirmOpen}
        title={t('billing.commercialMode.confirmTitle')}
        confirmLabel={t('billing.commercialMode.submit')}
        cancelLabel={t('tenantDetail.actions.cancel')}
        loading={submitting}
        onConfirm={(): void => {
          void submit();
        }}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
      >
        <Box variant="p">{to ? t(`billing.commercialMode.effect.${from}_${to}`) : null}</Box>
      </AdminConfirm>
    </>
  );
}
